import { useMemo, useState, type ReactNode } from 'react';
import type { Unit } from '@alexiarest/shared';
import { useSettings } from '../api/hooks';
import { purchaseUnitOptionsFor, unitOptionsFor } from '../labels';
import { SelectField } from './Field';

interface Props {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  /** `receta`: unidades de escandallo y rendimiento. `compra`: formatos de compra. */
  kind?: 'receta' | 'compra';
  hint?: ReactNode;
  disabled?: boolean;
}

/**
 * Selector de unidad que respeta el sistema de medidas de Ajustes (mejora 39,
 * Alex, 4 sep 2026): un restaurante metrico ve g, kg, mL, cL, L y pz; uno
 * imperial, oz, lb y las mismas de volumen y recuento. Nada de listas
 * mezcladas. Debajo, «Ver más unidades» ensena el catalogo entero para la
 * excepcion (la receta importada en libras de una cocina metrica).
 *
 * Si el valor guardado no esta entre las del sistema, se ensenan todas desde
 * el principio: un select con un valor que no esta en sus opciones se pinta
 * en blanco y parece que la receta no tiene unidad.
 */
export function UnitSelectField({
  label,
  value,
  onChange,
  kind = 'receta',
  hint,
  disabled,
}: Props): JSX.Element {
  const settings = useSettings();
  const system = settings.data?.unitSystem;
  const [todas, setTodas] = useState(false);

  const propias = useMemo(
    () => (kind === 'compra' ? purchaseUnitOptionsFor(system) : unitOptionsFor(system)),
    [kind, system],
  );
  const completas = useMemo(
    () => (kind === 'compra' ? purchaseUnitOptionsFor(system, true) : unitOptionsFor(system, true)),
    [kind, system],
  );
  const fueraDelSistema = value !== '' && !propias.some((o) => o.value === value);
  const ampliado = todas || fueraDelSistema;
  const options = ampliado ? completas : propias;

  return (
    <div className="field-unidad">
      <SelectField
        label={label}
        value={value as Unit}
        onChange={onChange}
        options={options}
        hint={hint}
        disabled={disabled}
      />
      {!fueraDelSistema && propias.length < completas.length && (
        <button
          type="button"
          className="btn btn--link btn--sm field__mas"
          onClick={() => setTodas((v) => !v)}
        >
          {todas ? 'Ver menos unidades' : 'Ver más unidades'}
        </button>
      )}
    </div>
  );
}
